import * as React from "react";
import { Dog, Cat, Bird, PawPrint, PencilSimple, Plus, Syringe, Warning, Heart } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { CustomerDetailLabels, PetRecord, VaccinationStatus } from "./types";
import { ageInYears, formatDateShort } from "./utils";

interface Props {
  pet: PetRecord;
  labels: CustomerDetailLabels;
  onEdit?: () => void;
  onNewAppointment?: () => void;
}

const speciesIcon = {
  dog: Dog,
  cat: Cat,
  bird: Bird,
  other: PawPrint,
} as const;

const vaxToneClass: Record<VaccinationStatus, string> = {
  current: "bg-biz-success-subtle text-biz-success border-biz-success-border",
  due_soon: "bg-biz-warning-subtle text-biz-warning border-biz-warning-border",
  overdue: "bg-biz-danger-subtle text-biz-danger border-biz-danger-border",
};

export const PetCard = React.forwardRef<HTMLDivElement, Props>(function PetCard(
  { pet, labels, onEdit, onNewAppointment },
  ref,
) {
  const SpeciesIcon = speciesIcon[pet.species];
  const age = ageInYears(pet.birthDate);

  return (
    <div
      ref={ref}
      className="rounded-biz-card border border-biz-border bg-biz-surface shadow-biz-sm hover:shadow-biz-md transition-shadow"
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="h-[72px] w-[72px] shrink-0 rounded-biz-card bg-biz-muted overflow-hidden flex items-center justify-center text-biz-foreground-muted">
            {pet.photoUrl ? (
              <img src={pet.photoUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <SpeciesIcon size={32} weight="regular" aria-hidden />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-display font-bold text-biz-foreground truncate">{pet.name}</h3>
              <span className="text-xs font-medium text-biz-foreground-muted">
                {labels.pet.species[pet.species]}
                {pet.breed ? ` · ${pet.breed}` : ""}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-x-3 gap-y-0.5 flex-wrap text-xs text-biz-foreground-muted">
              {pet.sex && <span>{labels.pet.sex[pet.sex]}</span>}
              {typeof age === "number" && <span className="font-mono-num">· {labels.pet.age(age)}</span>}
              {typeof pet.weightKg === "number" && (
                <span className="font-mono-num">· {labels.pet.weightKg(pet.weightKg)}</span>
              )}
              <span>· {pet.neutered ? labels.pet.neutered : labels.pet.notNeutered}</span>
            </div>
            {pet.microchipId && (
              <div className="mt-1 text-xs text-biz-foreground-subtle font-mono-num">
                {labels.pet.microchip(pet.microchipId)}
              </div>
            )}
          </div>
        </div>

        {/* Vaccinations */}
        <div className="mt-5">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-biz-foreground-subtle">
            <Syringe size={14} weight="regular" />
            {labels.pet.vaccinations}
          </div>
          {pet.vaccinations && pet.vaccinations.length > 0 ? (
            <ul className="mt-2 space-y-1.5">
              {pet.vaccinations.map((v) => (
                <li key={v.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-biz-foreground truncate">{v.name}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    {v.nextDueAt && (
                      <span className="text-xs text-biz-foreground-muted font-mono-num">
                        {labels.pet.nextDue(formatDateShort(v.nextDueAt))}
                      </span>
                    )}
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                        vaxToneClass[v.status],
                      )}
                    >
                      {labels.pet.vaccinationStatus[v.status]}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-biz-foreground-subtle italic">{labels.pet.noVaccinations}</p>
          )}
        </div>

        {/* Allergies + Chronic */}
        {((pet.allergies && pet.allergies.length > 0) ||
          (pet.chronicConditions && pet.chronicConditions.length > 0)) && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {pet.allergies && pet.allergies.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-biz-foreground-subtle">
                  <Warning size={14} weight="regular" />
                  {labels.pet.allergies}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {pet.allergies.map((a) => (
                    <span
                      key={a}
                      className="inline-flex items-center rounded-full bg-biz-danger-subtle text-biz-danger border border-biz-danger-border px-2 py-0.5 text-xs font-medium"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {pet.chronicConditions && pet.chronicConditions.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-biz-foreground-subtle">
                  <Heart size={14} weight="regular" />
                  {labels.pet.chronic}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {pet.chronicConditions.map((c) => (
                    <span
                      key={c}
                      className="inline-flex items-center rounded-full bg-biz-warning-subtle text-biz-warning border border-biz-warning-border px-2 py-0.5 text-xs font-medium"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Behavior */}
        {pet.behaviorNotes && (
          <div className="mt-4 rounded-biz-btn bg-biz-muted/60 px-3 py-2.5">
            <div className="text-xs font-semibold uppercase tracking-wider text-biz-foreground-subtle">
              {labels.pet.behavior}
            </div>
            <p className="mt-1 text-sm italic text-biz-foreground-muted leading-relaxed">
              “{pet.behaviorNotes}”
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-biz-border flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="h-11 px-3 rounded-biz-btn text-sm font-semibold text-biz-foreground-muted hover:bg-biz-muted inline-flex items-center gap-1.5 biz-focus-ring transition-colors"
        >
          <PencilSimple size={16} weight="regular" />
          {labels.pet.editPet}
        </button>
        <button
          type="button"
          onClick={onNewAppointment}
          className="h-11 px-3 rounded-biz-btn bg-biz-primary text-biz-primary-foreground hover:bg-biz-primary-hover text-sm font-semibold inline-flex items-center gap-1.5 biz-focus-ring transition-colors"
        >
          <Plus size={16} weight="regular" />
          {labels.pet.newAppointmentForPet(pet.name)}
        </button>
      </div>
    </div>
  );
});
